"use client";
import React, { useEffect, useState, useRef } from "react";
import dynamic from "next/dynamic";
import IMAGES from "@/data/assetData";
import AccessoriesModal from "@/components/ModalPopup/AccessoriesModal";
import { usefetchDataFromApi } from "@/utils/api";
import handleApiError from "@/utils/handleApiError";
import { useSelector, useDispatch } from "react-redux";
import CartSkeleton from "@/components/SkeletonDesign/CartSkeleton";

const LoginModal = dynamic(() => import("@/components/Login"), { ssr: false });
const RegisterModal = dynamic(() => import("@/components/Register"), { ssr: false });
import { calculateDaysHour } from "@/data/staticMethod";
import { formatDuration } from "@/utils/formatDuration";
import { getRentalDayBilling, RentalDayNote } from "@/components/ui/RentalDayNote";
import dayjs from "dayjs";
import { bumpCartVersion } from "@/redux/order/orderSlice";
import { trackBeginCheckout, trackAddPaymentInfo, toGa4Item } from "@/utils/analytics";

import Link from "next/link";
import Image from "next/image";
import Button from "@/components/ui/Button";
import Rupee from "@/components/ui/Rupee";
import {
  readPartnerBookingOrigin,
  readPartnerOriginSlug,
  clearPartnerBookingOrigin,
  PARTNER_WEB_BOOKING_PLATFORM,
} from "@/utils/partner/PartnerContext";
import { readGoogleClickIds } from "@/utils/gads";

// Inline stepper / delete icons (SVG so they render consistently across devices).
const IconMinus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /></svg>
);
const IconPlus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
);
const IconTrash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 5v6m4-6v6" /></svg>
);

// Mobile back-link header (fixed under the top contact bar). Declared at module
// level so it isn't recreated on every render. `basePath` keeps the back link
// inside the partner storefront when the visitor arrived from one (empty string
// on the global site, so the link stays /search there).
const MobileHeader = ({ basePath = "" }) => (
  <div className="lg:hidden min-h-12 px-4 fixed top-[14px] left-0 bg-white border-b border-gray-200 w-full shadow-sm flex gap-4 items-center text-black z-20">
    <Link href={`${basePath}/search`} aria-label="Back to search">
      <Image src={IMAGES.Chevron_icon} alt="" width={18} height={18} />
    </Link>
    <h2 className="text-base font-semibold leading-[27px] text-gray-900">Your Cart</h2>
  </div>
);

// "User tapped Pay but wasn't checkout-ready yet" — stored durably (sessionStorage)
// so the intent survives the page re-mount/navigation that the Register flow does
// (it router.push-es back to /cart). A 10-min TTL guarantees a stale flag can never
// silently auto-pay on a much later visit.
const PENDING_PAY_KEY = "sukuto_cart_pending_pay";
const PENDING_PAY_TTL = 10 * 60 * 1000; // 10 minutes
const markPendingPay = () => { try { sessionStorage.setItem(PENDING_PAY_KEY, String(Date.now())); } catch { /* storage unavailable */ } };
const clearPendingPay = () => { try { sessionStorage.removeItem(PENDING_PAY_KEY); } catch { /* storage unavailable */ } };
const hasPendingPay = () => {
  try {
    const t = Number(sessionStorage.getItem(PENDING_PAY_KEY));
    if (!t) return false;
    if (Date.now() - t > PENDING_PAY_TTL) { sessionStorage.removeItem(PENDING_PAY_KEY); return false; }
    return true;
  } catch { return false; }
};

export default function Cart() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cartVehicle, setCartVehicle] = useState([]);
  const [cName, setCName] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [cityName, setCityName] = useState("");
  const [totalRent, setTotalRent] = useState({});
  const [partialPaymentStatus, setPartialPaymentStatus] = useState(0);
  const [cartAvialable, setCartAvialable] = useState(false);
  const [paymentOpt, setPaymentOpt] = useState("1");
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const { isAuthenticated, userDetails } = useSelector((state) => state.user);
  const searchSelector = useSelector((state) => state.search);
  const [loading, setLoading] = useState(true);
  const [qtyLoading, setQtyLoading] = useState(false);
  const [idLoading, setIdLoading] = useState();
  const [qtyError, setQtyError] = useState(null);
  const [disabledBooking, setDisabledBooking] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  // Storefront prefix for in-funnel links when the visitor arrived from a
  // partner (read client-side from the sukuto_partner_origin cookie). Empty on
  // the global site, so links stay /search and / there. Set after mount to keep
  // SSR markup (always the global path) stable and avoid a hydration mismatch.
  const [basePath, setBasePath] = useState("");
  const { totalCartCount } = useSelector((state) => state.order);

  const dispatch = useDispatch();

  useEffect(() => {
    const slug = readPartnerOriginSlug();
    if (slug) setBasePath(`/partner/${slug}`);
  }, []);

  // begin_checkout fires once per cart visit, not on every quantity refresh.
  const checkoutTrackedRef = useRef(false);

  const openModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleCart = (statue) => {
    setLoading(statue);
    setQtyLoading(true);
    usefetchDataFromApi({
      url: "cart/index",
      method: "POST",
      data: { platform: 3 },
    }).then((res) => {
      if (res.success) {
        const { vehicles, cityName, start, end, rent, partialPaymentStatus } =
          res.data?.data || {};
        setStartDate(start);
        setEndDate(end);
        setCityName(cityName);
        setTotalRent(rent || {});
        setPartialPaymentStatus(partialPaymentStatus);
        setPaymentOpt(partialPaymentStatus == 1 ? "2" : "1");
        const finalVehicleList = [];
        const vCountName = [];

        for (const arrVehicleKey in vehicles) {
          finalVehicleList.push(vehicles[arrVehicleKey]);
          vCountName.push({
            count: vehicles[arrVehicleKey].count,
            vcompany: vehicles[arrVehicleKey].vehicle?.company,
            vname: vehicles[arrVehicleKey].vehicle?.name,
          });
        }
        setCName(vCountName);
        setLoading(false);
        setCartVehicle(finalVehicleList);
        setQtyLoading(false);

        if (!checkoutTrackedRef.current && finalVehicleList.length > 0) {
          checkoutTrackedRef.current = true;
          const r = rent || {};
          trackBeginCheckout({
            items: finalVehicleList.map((row, i) => toGa4Item(row, row?.rent?.total, i)),
            value: Number(getTotalAmt(r.total, r.tax, 0, r.discount)),
            // partialPaymentAmount = Sukuto commission only when partial payment
            // is supported; otherwise it's the full amount, so omit it.
            commission: partialPaymentStatus == 1 ? Number(r.partialPaymentAmount) : undefined,
          });
        }
      } else {
        setCName([]);
        setLoading(false);
        setCartVehicle([]);
        setQtyLoading(false);
      }
    }).catch(() => {
      setCName([]);
      setLoading(false);
      setCartVehicle([]);
      setQtyLoading(false);
    });
  };

  const getTotalAmt = (total, tax, deposit, discount) => {
    return ((total || 0) + (tax || 0) + (deposit || 0) + (discount || 0)).toFixed(2);
  };

  const handleAddToCart = (vehicleData, action) => {
    setIdLoading(vehicleData.id);
    setQtyLoading(true);
    setQtyError(null);
    let startDTime = `${searchSelector?.startDateTime?.date} ${searchSelector?.startDateTime?.time}`;
    let endDTime = `${searchSelector?.endDateTime?.date} ${searchSelector?.endDateTime?.time}`;
    usefetchDataFromApi({
      url: "cart/update",
      method: "POST",
      data: {
        key: vehicleData.key,
        search: {
          types: searchSelector?.vehicleType,
          start: startDTime,
          end: endDTime,
          cityId: searchSelector?.city?.id,
          platform: 3,
        },
        platform: 3,
        action: action,
      },
    }).then((res) => {
      if (res.success) {
        dispatch(bumpCartVersion());
        setQtyLoading(true);
        handleCart(false);
      } else {
        setQtyLoading(false);
        setIdLoading(undefined);
        setQtyError({ key: vehicleData.key, message: res.message || "Could not update the cart. Please try again." });
      }
    }).catch(() => {
      setQtyLoading(false);
      setIdLoading(undefined);
    });
  };

  // Logged in AND profile complete enough to confirm a booking.
  const isCheckoutReady = () =>
    isAuthenticated == true &&
    userDetails?.name != "" &&
    userDetails?.lastName != "" &&
    userDetails?.mobile?.length != 2 &&
    userDetails?.mobile != "" &&
    userDetails?.email?.length != 2 &&
    userDetails?.email != "";

  // The actual confirm + pay step (assumes checkout is ready).
  const proceedBooking = () => {
    setDisabledBooking(true);
    // /cart lives outside the /partner/<slug>/ route group, so the partner
    // context isn't available here. If this checkout started from a partner
    // storefront, the scoped search wrote a `sukuto_partner_origin` cookie;
    // read it to tag the booking as PLATFORM_PARTNER_WEB for attribution.
    const partnerOrigin = readPartnerBookingOrigin();
    const confirmData = {
      platform: 3,
      paymentType: paymentOpt,
    };
    if (partnerOrigin) {
      confirmData.bookingPlatform = PARTNER_WEB_BOOKING_PLATFORM;
      // Send the storefront's partner id so the server can VERIFY the
      // partner-web attribution before honouring it (the tag changes the
      // commission split, so it can't be trusted from the client alone). The
      // backend only applies PARTNER_WEB when this is a live white-label partner
      // that owns the cart's vehicles; otherwise it falls back to the standard
      // model. `partnerOrigin` is the partner id from the origin cookie.
      confirmData.partnerOriginId = partnerOrigin;
    }
    // Attach the Google Ads click id(s) captured on landing so the booking is
    // stored as ad-sourced — the retraction cron uses this to target only
    // conversions Google actually recorded. Absent for organic/direct visits.
    const clickIds = readGoogleClickIds();
    if (clickIds) {
      Object.assign(confirmData, clickIds);
    }
    usefetchDataFromApi({
      url: "booking/confirm",
      method: "POST",
      data: confirmData,
    }).then((res) => {
      if (res.success) {
        const { data } = res.data;
        // Booking row created with its origin persisted; clear the cookie so a
        // later global checkout isn't mis-attributed to this partner.
        clearPartnerBookingOrigin();
        setDisabledBooking(true);
        trackAddPaymentInfo({
          items: cartVehicle.map((row, i) => toGa4Item(row, row?.rent?.total, i)),
          value: Number(getTotalAmt(totalRent.total, totalRent.tax, 0, totalRent.discount)),
          commission: partialPaymentStatus == 1 ? Number(totalRent.partialPaymentAmount) : undefined,
          paymentType: paymentOpt,
        });
        clearPendingPay();
        handlePaymentRequest(data?.id);
        setCartAvialable(false);
      } else if (res.data?.status == -2) {
        clearPendingPay();
        setDisabledBooking(false);
        setCartAvialable(true);
      } else if (res.data?.status == -4) {
        // Server needs registration details — keep the intent so checkout
        // resumes automatically once the user finishes the Register screen.
        markPendingPay();
        setDisabledBooking(false);
        setIsRegisterModalOpen(true);
      } else {
        clearPendingPay();
        setDisabledBooking(false);
        setErrorMsg(res.message || "Something went wrong. Please try again.");
      }
    }).catch((err) => {
      clearPendingPay();
      setDisabledBooking(false);
      setErrorMsg(handleApiError(err, { fallback: "Something went wrong. Please try again." }));
    });
  };

  const handleBooking = () => {
    setErrorMsg("");
    if (isCheckoutReady()) {
      proceedBooking();
    } else {
      // Remember that the user is mid-checkout so we can auto-resume the booking
      // right after they authenticate — no need to tap "Pay" a second time.
      markPendingPay();
      if (
        (isAuthenticated == true && userDetails?.name == "") ||
        userDetails?.lastName == "" ||
        userDetails?.mobile?.length == 2 ||
        userDetails?.email?.length == 2 ||
        userDetails?.mobile == "" ||
        userDetails?.email == ""
      ) {
        setIsRegisterModalOpen(true);
        setIsLoginModalOpen(false);
      } else {
        setIsLoginModalOpen(true);
        setIsRegisterModalOpen(false);
      }
    }
  };

  // Auto-resume checkout once auth/profile completes (login or register), so the
  // pending "Pay" action continues automatically instead of requiring a 2nd tap.
  // Reads the durable flag, so it also fires on a fresh mount after the Register
  // flow navigates back to /cart.
  useEffect(() => {
    if (!hasPendingPay()) return;
    if (isCheckoutReady()) {
      clearPendingPay();
      setIsLoginModalOpen(false);
      setIsRegisterModalOpen(false);
      proceedBooking();
    } else if (isAuthenticated == true) {
      // Logged in but profile still incomplete → collect details, keep intent.
      setIsLoginModalOpen(false);
      setIsRegisterModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userDetails]);

  // Abandoned-cart lead capture is handled server-side: a sweep folded into the
  // existing payment-status cron raises a follow-up ticket for carts left with
  // items + a known mobile and no booking. That's reliable (no dependency on the
  // browser firing an unload event) and de-dups in the backend, so there is no
  // client-side push here. See CronController::scanAbandonedCarts.

  const handlePaymentRequest = (id) => {
    setDisabledBooking(true);
    usefetchDataFromApi({
      url: `payment/pay-booking?id=${id}&platform=3`,
      method: "GET",
    })
      .then((res) => {
        if (res.data?.razorpayOptions) {
          loadRazorpayCheckout(res.data.razorpayOptions);
        }
        else if (typeof res.data === 'string' && res.data.startsWith('http')) {
          setDisabledBooking(true);
          window.location = res.data;
        }
        else if (res.data?.unavailable) {
          // pay-booking re-checks availability: a vehicle in this booking has
          // since been taken. Re-enable the cart's availability UI so the
          // customer can adjust instead of retrying into the same dead end.
          clearPendingPay();
          setDisabledBooking(false);
          setCartAvialable(true);
          setErrorMsg("One of the vehicles in your cart is no longer available. Please review your cart and try again.");
        }
        else if (res.data?.status === -1 && typeof res.data?.data === 'string') {
          // Backend refusal with a specific reason (e.g. "This booking is no
          // longer active") — surface it instead of the generic message.
          setDisabledBooking(false);
          setErrorMsg(res.data.data);
        }
        else {
          setErrorMsg("Failed to initiate payment. Please try again.");
          setDisabledBooking(false);
        }
      })
      .catch((err) => {
        setDisabledBooking(false);
        setErrorMsg(handleApiError(err, { fallback: "Failed to initiate payment. Please try again." }));
      });
  };

  const loadRazorpayCheckout = (options) => {
    if (window.Razorpay) {
      openRazorpayCheckout(options);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      openRazorpayCheckout(options);
    };
    script.onerror = () => {
      setErrorMsg("Failed to load payment gateway. Please try again.");
      setDisabledBooking(false);
    };
    document.body.appendChild(script);
  };

  const openRazorpayCheckout = (options) => {
    try {
      const rzp = new window.Razorpay({
        ...options,
        handler: function (response) {
          try {
            const callbackUrl = new URL(options.callback_url);
            const orderId = callbackUrl.searchParams.get('orderId');
            const paymentType = callbackUrl.searchParams.get('paymentType');

            const frontendUrl = new URL(window.location.origin + '/booking');
            frontendUrl.searchParams.set('orderId', orderId);
            frontendUrl.searchParams.set('razorpay_payment_id', response.razorpay_payment_id);
            frontendUrl.searchParams.set('razorpay_order_id', response.razorpay_order_id);
            frontendUrl.searchParams.set('razorpay_signature', response.razorpay_signature);
            if (paymentType) {
              frontendUrl.searchParams.set('paymentType', paymentType);
            }

            window.location.href = frontendUrl.toString();
          } catch {
            setErrorMsg("Payment was processed but redirect failed. Please check your bookings.");
            setDisabledBooking(false);
          }
        },
        modal: {
          ondismiss: function() {
            setDisabledBooking(false);
          }
        }
      });

      rzp.on('payment.failed', function (response) {
        setErrorMsg(`Payment failed: ${response?.error?.description || "Please try again."}`);
        setDisabledBooking(false);
      });

      rzp.open();
    } catch {
      setErrorMsg("Failed to open payment gateway. Please try again.");
      setDisabledBooking(false);
    }
  };

  useEffect(() => {
    const id = setTimeout(() => {
      handleCart(true);
    }, 0);
    return () => clearTimeout(id);
  }, []);

  // The order total and the amount actually charged now (full vs partial payment).
  const orderTotal = getTotalAmt(totalRent.total, totalRent.tax, 0, totalRent.discount);
  const amountPayableNow =
    paymentOpt == "1" ? orderTotal : (totalRent.partialPaymentAmount ?? 0);

  const cartRentalDays = cartVehicle.map((item) => getRentalDayBilling(startDate, endDate, { ...item?.rent, rate: item?.rent?.rate || item?.rate })?.days || null);
  const summaryRentalDays = cartRentalDays.length && cartRentalDays.every((d) => d && d === cartRentalDays[0]) ? cartRentalDays[0] : null;

  return (
    <>
      <main>
        <h1 className="sr-only">Your Cart</h1>
        {loading && <CartSkeleton />}

        {!loading && cartVehicle && cartVehicle?.length == 0 && (
          <>
            <MobileHeader basePath={basePath} />
            <div className="min-h-[calc(100vh-60px)] flex items-center justify-center px-[6%]">
              <div className="text-center max-w-sm">
                <Image
                  width={96}
                  height={96}
                  className="mx-auto"
                  src={IMAGES.emptyCart}
                  alt="Empty cart"
                />
                <h2 className="font-poppins text-xl font-semibold text-textPrimary mt-4">
                  Your cart is empty
                </h2>
                <p className="text-sm text-textSecondary mt-1.5 mb-5 leading-relaxed">
                  Search, compare, add to cart, book &amp; enjoy your ride.
                </p>
                <Button href={`${basePath}/`} size="md">Browse vehicles</Button>
              </div>
            </div>
          </>
        )}

        {cartVehicle.length > 0 && (
          <>
            <MobileHeader basePath={basePath} />

            <div className="flex justify-between gap-4 mt-20 lg:mt-[88px] cart-section flex-col lg:flex-row px-[3%] lg:px-[4%] pb-[110px] lg:pb-8 min-h-[calc(100vh-140px)] lg:min-h-[calc(100vh-148px)]">
              <AccessoriesModal closeModal={closeModal} isModalOpen={isModalOpen} />

              {/* ===== Vehicle items ===== */}
              <div className="w-full lg:w-[75%]">
                {cartVehicle.map((item, i) => {
                  const { count, countRent, location, rate, vehicle, rent, key, deliveryPoint } = item;
                  const billing = getRentalDayBilling(startDate, endDate, { ...rent, rate: rent?.rate || rate });
                  return (
                    <div
                      className="bg-white rounded-card border border-gray-200 shadow-card-sm p-3.5 mb-4"
                      key={i}
                    >
                      {/* header: name + location */}
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-1 pb-3 border-b border-gray-100">
                        <span className="text-base font-semibold text-gray-900">
                          {vehicle?.company} {vehicle?.name}
                        </span>
                        <span className="flex items-center gap-1.5 text-sm text-gray-500 min-w-0">
                          <Image src={IMAGES.redarrow_icon} width={14} height={14} alt="" className="shrink-0" />
                          <span className="truncate">{location?.name}</span>
                        </span>
                      </div>

                      <div className="flex flex-col lg:flex-row gap-3 pt-3">
                        {/* vehicle + qty + charges (first on mobile) */}
                        <div className="w-full lg:w-1/3 order-1 lg:order-2">
                          <div className="flex items-center gap-3">
                            {vehicle?.imageUrl && (
                              <Image
                                src={vehicle.imageUrl}
                                alt={`${vehicle?.company} ${vehicle?.name}`}
                                className="w-28 h-auto rounded-md"
                                width={320}
                                height={240}
                              />
                            )}
                            <div className="flex items-center gap-1.5 ml-auto">
                              <button
                                disabled={qtyLoading}
                                aria-label="Decrease quantity"
                                onClick={() => handleAddToCart(item, "remove")}
                                className="w-10 h-10 rounded-full border border-gray-200 grid place-items-center text-gray-700 transition-colors hover:enabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:w-4 [&_svg]:h-4"
                              >
                                <IconMinus />
                              </button>
                              <span className="w-8 text-center text-sm font-semibold text-gray-900">
                                {qtyLoading && idLoading == item.id ? (
                                  <Image src={IMAGES.Spinner2} alt="loading" width={16} height={16} className="w-4 h-4 mx-auto" />
                                ) : (
                                  count
                                )}
                              </span>
                              <button
                                disabled={qtyLoading}
                                aria-label="Increase quantity"
                                onClick={() => handleAddToCart(item, "add")}
                                className="w-10 h-10 rounded-full border border-gray-200 grid place-items-center text-gray-700 transition-colors hover:enabled:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:w-4 [&_svg]:h-4"
                              >
                                <IconPlus />
                              </button>
                              <button
                                disabled={qtyLoading}
                                aria-label="Remove vehicle"
                                onClick={() => handleAddToCart(item, "delete")}
                                className="w-10 h-10 rounded-full grid place-items-center text-gray-400 transition-colors hover:enabled:text-red-500 hover:enabled:bg-red-50 disabled:opacity-50 [&_svg]:w-[18px] [&_svg]:h-[18px]"
                              >
                                <IconTrash />
                              </button>
                            </div>
                          </div>
                          {qtyError?.key === key && (
                            <p role="alert" className="mt-1.5 text-xs font-medium text-red-600 text-right">{qtyError.message}</p>
                          )}

                          <div className="mt-3 space-y-1.5">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Rent ({count})</span>
                              <span className="font-semibold text-gray-900"><Rupee />{countRent?.gross}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Deposit <span className="text-xs">(refundable)</span></span>
                              <span className="font-medium text-gray-700"><Rupee />{rate?.deposit}<span className="text-gray-400"> /vehicle</span></span>
                            </div>
                            {deliveryPoint?.fee > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Delivery fee <span className="text-xs">(not refundable)</span></span>
                                <span className="font-medium text-gray-700"><Rupee />{deliveryPoint.fee}<span className="text-gray-400"> /vehicle</span></span>
                              </div>
                            )}
                          </div>
                          <RentalDayNote billing={billing} className="mt-3" />
                        </div>

                        {/* terms + ride limits */}
                        <div className="w-full lg:w-2/3 lg:pr-4 lg:border-r lg:border-gray-100 order-2 lg:order-1">
                          <h3 className="text-sm font-semibold text-gray-800 mb-2">
                            Partner Terms &amp; Conditions
                          </h3>
                          <ul className="list-disc ml-5 space-y-1">
                            {rate?.terms?.map((term, ti) => (
                              <li className="text-sm font-normal text-gray-600" key={ti}>
                                {term}
                              </li>
                            ))}
                          </ul>
                          <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3">
                            {rent?.freeKm && (
                              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                                <Image src={IMAGES.MountainIcon} alt="" width={20} height={20} />
                                Free {rent.freeKm} km
                              </span>
                            )}
                            {rate?.excessFee && (
                              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                                <Image src={IMAGES.MoneyIcon} alt="" width={20} height={20} />
                                <Rupee />{rate.excessFee}/extra km
                              </span>
                            )}
                            {rate?.speedLimit && (
                              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                                <Image src={IMAGES.SpeedIcon} alt="" width={20} height={20} />
                                {rate.speedLimit} km/h limit
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ===== Order summary ===== */}
              <div className="w-full lg:w-[25%]" id="order-summary">
                <div className="bg-white rounded-card border border-gray-200 shadow-card-sm overflow-hidden lg:sticky lg:top-[88px]">
                  <h2 className="text-base font-semibold text-gray-900 px-4 pt-3.5 pb-2">Order Summary</h2>

                  {/* trip */}
                  <div className="px-4 pb-3 border-b border-gray-100 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-gray-700">{cityName}</span>
                      <span className="font-medium text-gray-700 text-right">
                        {formatDuration(calculateDaysHour(startDate, endDate))}
                        {summaryRentalDays && <span className="block text-xs font-normal text-gray-500">billed as {summaryRentalDays} rental days</span>}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Pick up</span>
                      <span className="text-gray-700">{dayjs(startDate).format("D MMM YY, hh:mm A")}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Drop off</span>
                      <span className="text-gray-700">{dayjs(endDate).format("D MMM YY, hh:mm A")}</span>
                    </div>
                    {cName.map((c, ci) => (
                      <div className="text-sm text-gray-500 pt-0.5" key={ci}>
                        ×{c.count} {c?.vcompany} {c.vname}
                      </div>
                    ))}
                  </div>

                  {/* charges */}
                  <div className="px-4 py-3 border-b border-gray-100 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Total Rent</span>
                      <span className="text-gray-700"><Rupee />{totalRent.total != null ? totalRent.total.toFixed(2) : '0.00'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Taxes</span>
                      <span className="text-gray-700"><Rupee />{totalRent.tax != null ? totalRent.tax.toFixed(2) : '0.00'}</span>
                    </div>
                    {Number(totalRent.discount) > 0 && (
                      <div className="flex justify-between text-sm text-emerald-600">
                        <span>Discount</span>
                        <span>− <Rupee />{Number(totalRent.discount).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Refundable deposit <span className="text-xs">(at pickup)</span></span>
                      <span className="text-gray-700"><Rupee />{totalRent.deposit ?? 0}</span>
                    </div>
                    {totalRent.handoverFee > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Delivery fee <span className="text-xs">(at pickup, not refundable)</span></span>
                        <span className="text-gray-700"><Rupee />{totalRent.handoverFee}</span>
                      </div>
                    )}
                  </div>

                  {/* payment option */}
                  <form className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-700 mb-2">Payment option</p>
                    {partialPaymentStatus == 1 && (
                      <label htmlFor="cart-payment-partial" className="flex items-start gap-3 mb-2 p-2 rounded-lg border border-gray-200 hover:border-primary cursor-pointer transition-colors">
                        <input
                          id="cart-payment-partial"
                          type="radio"
                          name="payment-option"
                          value="2"
                          checked={paymentOpt == "2"}
                          onChange={(e) => setPaymentOpt(e.target.value)}
                          className="mt-0.5 h-4 w-4 rounded-full border-gray-400 accent-primary focus:ring-primary"
                        />
                        <span>
                          <div className="font-medium text-sm text-gray-900">Partial Payment</div>
                          <p className="text-[11px] text-gray-500">Pay part now to secure your booking</p>
                        </span>
                      </label>
                    )}
                    <label htmlFor="cart-payment-full" className="flex items-start gap-3 p-2 rounded-lg border border-gray-200 hover:border-primary cursor-pointer transition-colors">
                      <input
                        id="cart-payment-full"
                        type="radio"
                        name="payment-option"
                        value="1"
                        checked={paymentOpt == "1"}
                        onChange={(e) => setPaymentOpt(e.target.value)}
                        className="mt-0.5 h-4 w-4 rounded-full border-gray-400 accent-primary focus:ring-primary"
                      />
                      <span>
                        <div className="font-medium text-sm text-gray-900">Pay Now</div>
                        <p className="text-[11px] text-gray-500">Pay the full amount now</p>
                      </span>
                    </label>
                  </form>

                  {/* totals */}
                  <div className="px-4 py-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Order total</span>
                      <span className="font-semibold text-gray-900"><Rupee />{orderTotal}</span>
                    </div>

                    {paymentOpt == "2" && (
                      <>
                        <div className="flex justify-between text-sm text-primary">
                          <span>Advance payment</span>
                          <span className="font-semibold"><Rupee />{totalRent.partialPaymentAmount ?? 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">
                            Remaining rent
                            <span className="block text-xs text-gray-400">Pay on or before pickup</span>
                          </span>
                          <span className="font-medium text-gray-700"><Rupee />{totalRent.remainingPaymentAmount ?? 0}</span>
                        </div>
                      </>
                    )}

                    <div className="flex justify-between items-baseline pt-2.5 border-t border-gray-200">
                      <span className="text-sm font-semibold text-gray-900">Amount payable now</span>
                      <span className="text-lg font-bold text-gray-900"><Rupee />{amountPayableNow}</span>
                    </div>
                  </div>

                  {/* Reassurance at the highest-intent step (reduces final-step drop-off) */}
                  <p className="px-4 pb-3 text-[11px] text-gray-500 text-center leading-snug">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="inline-block align-[-2px] mr-1">
                      <path d="M12 2l7 4v6c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6l7-4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Secure payment · Refundable deposit collected at pickup
                  </p>

                  {/* Consent. Until this existed there was no record anywhere that a
                      customer had seen the cancellation policy — which made every
                      no-refund clause arguable. Keep it adjacent to the CTA. */}
                  <p className="px-4 pb-3 text-[11px] text-gray-500 text-center leading-snug">
                    By continuing you agree to our{" "}
                    <Link href="/legal/terms-of-use/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">Terms of Use</Link>
                    {" "}and{" "}
                    <Link href="/legal/refund-cancellation-policy/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">Cancellation &amp; Refund Policy</Link>.
                  </p>

                  {/* mobile-only "add more" affordance (desktop has it in the CTA block below) */}
                  <div className="px-4 pb-1 lg:hidden">
                    <Button variant="link" href={`${basePath}/search`} block>+ Add more vehicles</Button>
                  </div>

                  {(errorMsg || cartAvialable) && (
                    <div className="mx-4 mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-sm text-center">
                      {errorMsg || "Some vehicles are sold out / unavailable"}
                    </div>
                  )}

                  {/* desktop CTAs — mobile uses the sticky bar below */}
                  <div className="px-4 pb-4 hidden lg:block">
                    <Button block size="md" loading={disabledBooking} onClick={() => handleBooking()} className="mb-2">
                      {disabledBooking ? "Processing..." : <>Pay <Rupee />{amountPayableNow} &amp; Book Now</>}
                    </Button>
                    <Button variant="link" href={`${basePath}/search`} block>Add more vehicles</Button>
                  </div>
                </div>

                <div className="hidden lg:block" />
              </div>
            </div>

            {/* ===== Mobile sticky checkout bar (the bottom nav is hidden on /cart,
                 so this anchors cleanly to the bottom edge) ===== */}
            <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
              {(errorMsg || cartAvialable) && (
                <div className="bg-red-50 text-red-600 text-xs px-4 py-2 text-center">
                  {errorMsg || "Some vehicles are sold out / unavailable"}
                </div>
              )}
              <div className="px-4 pt-2.5 pb-3">
                <Button block size="md" loading={disabledBooking} onClick={() => handleBooking()}>
                  {disabledBooking ? "Processing..." : <>Pay <Rupee />{amountPayableNow} &amp; Book Now</>}
                </Button>
                {/* Repeated here because the sticky bar covers the summary's copy on mobile,
                    which is 90% of traffic — the consent has to be where the tap happens. */}
                <p className="mt-2 text-[10px] leading-snug text-gray-500 text-center">
                  By continuing you agree to our{" "}
                  <Link href="/legal/terms-of-use/" target="_blank" rel="noopener noreferrer" className="underline">Terms</Link>
                  {" "}and{" "}
                  <Link href="/legal/refund-cancellation-policy/" target="_blank" rel="noopener noreferrer" className="underline">Cancellation &amp; Refund Policy</Link>.
                </p>
              </div>
            </div>

            <div className="cart-login">
              {isLoginModalOpen && (
                <LoginModal
                  setIsLoginModalOpen={setIsLoginModalOpen}
                  setIsRegisterModalOpen={setIsRegisterModalOpen}
                  location={"/cart"}
                />
              )}
              {isRegisterModalOpen && (
                <RegisterModal
                  setIsRegisterModalOpen={setIsRegisterModalOpen}
                  location={"/cart"}
                />
              )}
            </div>
          </>
        )}
      </main>
    </>
  );
}
